import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyRowPatch,
  createTask,
  isPendingTask,
  nextPendingTaskId,
  optimisticTask,
  updateTask,
  type RowPatch,
  type Task,
  type TaskInput,
  type TaskSummary,
} from '../../lib/tasks';
import { defaultOwnerId, type Assignee } from '../../lib/assignees';
import type { Member } from '../../lib/users';
import { mapPool } from '../../lib/pool';
import { putTask } from '../../lib/taskCache';
import { onSeriesChanged } from '../../lib/recurrence';
import { bulkStatusChangedMessage, statusChangedMessage } from '../../lib/taskStatus';
import { useToast } from '../toast';
import { useRowFlash } from './useRowFlash';

/**
 * Everything a task list does to its rows, minus which rows it is looking at.
 *
 * Lifted out of ProjectTasks when the cross-project Tasks page arrived. The two
 * pages disagree about exactly four things -- what they fetch, what they file a
 * new task into, where a row links to, and what order it renders in -- and agree
 * about all of this: the optimistic apply, the stamp that stops an overtaken
 * reply rolling a row back, the pending-create swap, the bulk pool, and the
 * shift-click anchor.
 *
 * That list is the argument for the hook. It is the subtle half of the feature,
 * and the failure mode of a second copy is not a compile error -- it is one page
 * quietly getting last-write-wins slightly wrong a year from now. The same
 * reasoning lib/taskGroups.ts gives for not letting two loops decide row order.
 *
 * What deliberately stays with the page: anything touching the URL, the router
 * or the fetch itself. Those are exactly the four things that differ, and
 * threading them in here as options would have been the same duplication with
 * more indirection.
 */

/** Referentially stable, so clearing an already-empty selection is not a
 *  re-render. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set();

/**
 * How many bulk PATCHes are allowed in the air at once: one.
 *
 * Every PATCH runs its own serializable transaction, and concurrent ones
 * conflict even when they touch different tasks. Measured against eight tasks on
 * the real database, all eight land when they go one at a time and half of them
 * are rejected at six. The server retries a conflict once, which covers two
 * people saving at the same moment; it does not cover a batch racing itself.
 *
 * The cost is wall-clock -- a round trip each, in series -- and it is paid
 * behind an optimistic list, so the rows change the instant they are clicked
 * and only the confirmations trickle in.
 */
const BULK_CONCURRENCY = 1;

/**
 * A create that is on its way.
 *
 * `pendingId` is the row already on screen, so a caller keeping a list of its
 * own -- the panel's subtasks -- can show the same task in the same instant and
 * swap it when `result` answers. `result` resolves to the saved task, or to null
 * when it failed and the page has taken the row back.
 */
export type PendingCreate = { pendingId: string; result: Promise<Task | null> };

/** A draft, plus the project to file it into where the page has more than one. */
export type TaskDraft = Partial<TaskInput> & { title: string; projectId?: string };

export type CreateInBackground = (
  draft: TaskDraft,
  options?: { reportFailure?: boolean },
) => PendingCreate;

export type FailedCreate = { id: number; input: TaskInput; projectId: string };

/** Keys the failed-create banners. Nothing outside this module ever sees it, and
 *  a failed draft has no id of its own to key by. */
let failureCount = 0;
const nextFailureId = () => ++failureCount;

export function useTaskListState({
  load,
  loadDeps,
  resetSelectionOn,
  defaultProjectId,
  projectFor,
  orderFor,
  assignees,
  user,
}: {
  /** One page of rows. The hook owns the abort; the page owns the query. */
  load: (signal: AbortSignal) => Promise<{ tasks: TaskSummary[]; total?: number }>;
  /** What a re-fetch keys on. Must keep a stable length between renders. */
  loadDeps: unknown[];
  /** What counts as "a different list", so the selection is dropped. Narrower
   *  than `loadDeps` on purpose: re-ordering the same rows is the same list. */
  resetSelectionOn: unknown[];
  /** Where a create goes when the draft does not say. Null on a page that
   *  always asks. */
  defaultProjectId: string | null;
  /** The project's name, for the optimistic row's label. */
  projectFor: (projectId: string) => { id: string; name: string } | null;
  /** The on-screen order, for shift-click ranges. A callback rather than the
   *  ordered array because the page derives that from `tasks`, which comes from
   *  here -- passing the array in would be a cycle. */
  orderFor: (tasks: TaskSummary[]) => TaskSummary[];
  assignees: Assignee[];
  user: Member | null;
}) {
  const [tasks, setTasks] = useState<TaskSummary[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * A re-fetch of the list that is already on screen, kept apart from
   * `loading`.
   *
   * `loading` puts the skeleton up in place of the rows, which is right when
   * there are no rows yet and wrong when somebody pressed Refresh: throwing the
   * list away for the length of a round trip loses the scroll position and the
   * row they were looking at, to show them nothing.
   */
  const [refreshing, setRefreshing] = useState(false);
  /** Bumped to re-run the load effect with the query unchanged. */
  const [reloadToken, setReloadToken] = useState(0);
  /** Set by `refresh` and read by the effect, so the effect can tell a manual
   *  re-fetch from a first load of a list nobody has seen. */
  const manualRefresh = useRef(false);
  /**
   * Two kinds of failure, kept apart because they want opposite treatment.
   *
   * A load failure means there is no list to show, so it replaces the list. An
   * action failure -- a row edit, a bulk change -- happens *over* a list that is
   * on screen and still correct apart from the row that just reverted; showing
   * it in the list's place would blank the page underneath the thing you were
   * editing.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  /**
   * Creates that came back with an error, with the draft still attached so it
   * can be sent again. Kept as a list rather than one slot: several can be in
   * flight at once, and a second failure must not quietly drop the first
   * person's typing.
   */
  const [failedCreates, setFailedCreates] = useState<FailedCreate[]>([]);
  /**
   * Rows ticked for a bulk action. Raw, and not necessarily all on screen --
   * see `selected` below, which is the set anything else should read.
   */
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(EMPTY_SELECTION);
  /** Where the last plain click landed, for shift-click ranges. */
  const selectionAnchor = useRef<string | null>(null);
  /**
   * The last patch sent per task, so a reply that has been overtaken can be
   * dropped instead of putting a stale value back on screen.
   *
   * The priority cell is designed to be clicked repeatedly, and without this
   * the first reply lands after the second optimistic apply and visibly rolls
   * it back. Same idea as TaskDetail's `latestSave`, keyed by id because a
   * list has many rows in flight where the panel has one task.
   */
  const patchStamps = useRef(new Map<string, number>());

  /**
   * The three callbacks, read through refs.
   *
   * They are rebuilt on most renders by the pages that pass them, and putting
   * them in a dependency array would re-run the fetch on every keystroke. The
   * effects key on the plain values in `loadDeps` instead, which is what
   * actually decides whether the answer would differ.
   */
  const loadRef = useRef(load);
  const orderForRef = useRef(orderFor);
  const projectForRef = useRef(projectFor);
  loadRef.current = load;
  orderForRef.current = orderFor;
  projectForRef.current = projectFor;

  useEffect(() => {
    const controller = new AbortController();
    // Read here rather than in `refresh`, because the effect is what actually
    // runs the fetch -- and cleared immediately, so a filter changed a moment
    // later is a first load of a different list rather than another refresh.
    const manual = manualRefresh.current;
    manualRefresh.current = false;

    if (manual) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setRefreshing(false);
    }

    loadRef
      .current(controller.signal)
      .then((data) => {
        setTasks(data.tasks);
        setTotal(data.total ?? null);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        const message =
          requestError instanceof Error ? requestError.message : 'Could not load tasks.';
        // A refresh that fails is the action-failure case, not the load-failure
        // one: the rows underneath are still on screen and still correct, and
        // `loadError` renders *instead of* them -- so reporting it there would
        // blank the list because the reload went wrong, not the list.
        if (manual) setActionError(message);
        else setLoadError(message);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...loadDeps, reloadToken]);

  /**
   * Ask the server for this same list again.
   *
   * Everything on these pages is optimistic and local, so a list left open goes
   * stale the moment somebody else touches a task -- and nothing here polls.
   * This is the way back to the server without navigating away and losing the
   * filters, and it deliberately re-runs the same effect rather than fetching
   * on its own: one place owns the abort, the ordering and the error split.
   */
  const refresh = useCallback(() => {
    manualRefresh.current = true;
    setReloadToken((token) => token + 1);
  }, []);

  /**
   * A repeating task brought on its next instance, or an Undo took one away.
   *
   * Refetched rather than inserted: only the server's query knows whether the
   * new row matches this page's filters, and re-deriving them here would be a
   * second definition of the list. Through `refresh`, so the rows stay on
   * screen while it lands.
   */
  useEffect(() => onSeriesChanged(refresh), [refresh]);

  /**
   * A different list means a different selection. Deriving alone would not do
   * it: the ids stay in `selectedIds` while they are filtered out, so clearing
   * the filter would bring a forgotten selection back to life.
   */
  useEffect(() => {
    setSelectedIds(EMPTY_SELECTION);
    selectionAnchor.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetSelectionOn);

  /**
   * The selection, narrowed to rows that are actually on screen and actually
   * saved.
   *
   * Derived rather than pruned in an effect: an effect runs a render late, and
   * that render is the one where the bar says "3 selected" over a list of one.
   */
  const selected = useMemo(() => {
    const live = new Set<string>();
    for (const task of tasks) {
      if (selectedIds.has(task.id) && !isPendingTask(task.id)) live.add(task.id);
    }
    return live;
  }, [tasks, selectedIds]);

  const upsertTask = useCallback((task: TaskSummary) => {
    setTasks((current) =>
      current.some((entry) => entry.id === task.id)
        ? current.map((entry) => (entry.id === task.id ? task : entry))
        : [...current, task],
    );
  }, []);

  /** The rows as they stand, for the callbacks below that have to read them
   *  without being rebuilt every time one changes. */
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const removeTask = useCallback((taskId: string) => {
    // `total` is the server's count for this query, and it is the whole of what
    // decides whether "Showing N of M" and Load more appear. A row taken out
    // here is one the server would no longer return either -- it was deleted,
    // or it no longer matches the filter -- so the count goes with it, and the
    // list does not start claiming it is truncated because a task was ticked
    // off. Pending rows were never counted, and neither is a second removal of
    // something already gone.
    const counted = !isPendingTask(taskId) && tasksRef.current.some((entry) => entry.id === taskId);

    setTasks((current) =>
      current
        .filter((entry) => entry.id !== taskId)
        // Mirrors the server's ON DELETE SET NULL, so promoted subtasks do not
        // appear to vanish until the next reload.
        .map((entry) => (entry.parentId === taskId ? { ...entry, parentId: null } : entry)),
    );

    if (counted) setTotal((current) => (current === null ? null : Math.max(0, current - 1)));
  }, []);

  /**
   * Create a task without making anyone watch the round trip.
   *
   * The row goes into the list immediately under a pending id and is swapped for
   * the server's row when it lands -- in place, so it does not jump position on
   * arrival. A create takes over a second against RDS, and blocking the panel
   * for it is the difference between typing a list of tasks and filing them one
   * at a time.
   *
   * This lives here rather than in the panel on purpose: the panel closes the
   * instant Create is pressed, and a request owned by an unmounted component has
   * nowhere to report a failure.
   */
  const createInBackground = useCallback<CreateInBackground>(
    (
      draft,
      // The panel handles its own subtask failures, inline where the subtask was
      // typed. The banner on the page sits behind the panel's scrim, which
      // swallows every click outside the panel -- so Try again over there would
      // be unreachable without closing the panel first.
      options = {},
    ): PendingCreate => {
      const { projectId: draftProjectId, ...fields } = draft;
      const projectId = draftProjectId ?? defaultProjectId;
      const pendingId = nextPendingTaskId();

      // Only reachable if a page wires this up without either a default or a
      // picker, which is a bug rather than something a person can do.
      if (!projectId) {
        setActionError('That task has no project to go in.');
        return { pendingId, result: Promise.resolve(null) };
      }

      const byId = new Map(assignees.map((assignee) => [assignee.id, assignee]));

      // The panel sends every field. The inline rows send a title and the group
      // they were typed in and mean "the usual" for the rest, so the rest is
      // filled in here -- in one place, where the row that goes on screen and
      // the request that goes to the server cannot disagree about it.
      //
      // The owner is the exception. A roster that has not loaded yet means we do
      // not know who the starred person is, and guessing `null` would create the
      // task unowned -- so the key is dropped from the request instead and the
      // server applies the same default it applies to any owner-less create.
      const ownerKnown = fields.ownerId !== undefined || assignees.length > 0;
      const ownerId = fields.ownerId !== undefined ? fields.ownerId : defaultOwnerId(assignees);

      const input: TaskInput = {
        description: null,
        tags: [],
        status: 'TODO',
        priority: 'MEDIUM',
        startDate: null,
        dueDate: null,
        remindAt: null,
        repeat: null,
        collaboratorIds: [],
        parentId: null,
        ...fields,
        ownerId,
      };

      upsertTask(
        optimisticTask(
          pendingId,
          // A project the page cannot name is still a project the task belongs
          // to, so the row goes in labelled with the id rather than not at all.
          projectForRef.current(projectId) ?? { id: projectId, name: '' },
          input,
          {
            owner: ownerId ? (byId.get(ownerId) ?? null) : null,
            collaborators: input.collaboratorIds
              .map((id) => byId.get(id))
              .filter((assignee): assignee is Assignee => assignee !== undefined),
            // The list only ever renders this as an avatar on hover-less
            // columns, but leaving it null would make the optimistic row differ
            // from the real one in a field somebody will eventually read.
            createdBy: user,
          },
        ),
      );

      // `undefined` is dropped by JSON.stringify, so this is how the key is left
      // out rather than sent as null -- which the server reads as "nobody",
      // not as "you decide".
      const payload = { ...input, ...(ownerKnown ? {} : { ownerId: undefined }) };

      const result = createTask(projectId, payload)
        .then(({ task }) => {
          // Replaced where it stands rather than removed and appended: the row
          // is on screen, and moving it to the bottom of the group for no reason
          // is exactly the flicker the optimistic row exists to avoid.
          setTasks((current) => current.map((entry) => (entry.id === pendingId ? task : entry)));
          // The panel is the likeliest next stop for a task somebody just
          // typed, and this is the whole of its detail already in hand.
          putTask(task);
          return task;
        })
        .catch(() => {
          removeTask(pendingId);
          // The draft rides along so Try again can re-send it -- whatever typed
          // it is long gone, and losing somebody's typing to a dropped
          // connection is not an acceptable trade for the speed.
          if (options.reportFailure !== false) {
            setFailedCreates((current) => [
              ...current,
              { id: nextFailureId(), input, projectId },
            ]);
          }
          // Answered rather than rethrown: the failure is already handled and
          // on screen, and an unhandled rejection helps nobody.
          return null;
        });

      return { pendingId, result };
    },
    [defaultProjectId, assignees, user, upsertTask, removeTask],
  );

  const dismissCreate = useCallback((id: number) => {
    setFailedCreates((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const retryCreate = useCallback(
    (failure: FailedCreate) => {
      dismissCreate(failure.id);
      createInBackground({ ...failure.input, projectId: failure.projectId });
    },
    [dismissCreate, createInBackground],
  );

  /** Bumps a task's stamp and returns it, so a reply can check it is still the
   *  most recent thing anyone asked for. */
  const stamp = useCallback((taskId: string): number => {
    const next = (patchStamps.current.get(taskId) ?? 0) + 1;
    patchStamps.current.set(taskId, next);
    return next;
  }, []);

  const { toast } = useToast();
  const { flashId, flash } = useRowFlash();

  /**
   * Sets a status back, for a toast's Undo -- but only if the task still has
   * the status the toast is about. Something else moving it since (another
   * row control, the panel, a second toast's Undo) is the newer word, and an
   * old toast must not overwrite it.
   */
  const undoStatus = useCallback(
    (fallback: TaskSummary, from: RowPatch['status'], to: NonNullable<RowPatch['status']>) => {
      const current = tasksRef.current.find((entry) => entry.id === fallback.id) ?? fallback;
      if (current.status !== from) return;
      void patchTaskRef.current(current, { status: to }, { undoable: false });
    },
    [],
  );

  /**
   * One inline edit, applied optimistically.
   *
   * Every row control goes through here. The row is already on screen and a
   * round trip would make a click feel broken, so the change lands first and
   * the request follows; a failure puts the row back the way it was and says
   * what the server said, which for the start-date rule is the only sentence
   * that tells anyone what to do about it.
   *
   * Note that this does re-sort, in the same paint. Due-date order is a
   * property of the list rather than of a fetch (lib/taskGroups.ts), so a due
   * date edited here moves its row immediately -- out from under the cursor,
   * which is why the row flashes where it lands. The alternative is a list
   * quietly disagreeing with its own stated order until someone reloads it.
   */
  const patchTask = useCallback(
    async (task: TaskSummary, patch: RowPatch, { undoable = true }: { undoable?: boolean } = {}) => {
      const sent = stamp(task.id);

      upsertTask(applyRowPatch(task, patch));
      setActionError(null);
      // On the click, not the reply: the row moves now, so now is when it
      // needs finding.
      if (patch.dueDate !== undefined && patch.dueDate !== task.dueDate) flash(task.id);

      try {
        const { task: updated, spawned } = await updateTask(task.id, patch);
        if (patchStamps.current.get(task.id) === sent) {
          upsertTask(updated);
          // Kept in step so a row edited from the list does not open showing the
          // status it had before, in the one case the list row cannot cover it:
          // a filter that hides the row leaves the cache as the only copy.
          putTask(updated);
        }
        // Raised on the reply rather than the click: a change that failed has
        // already been put back, and offering to undo it would be a lie.
        if (undoable && patch.status !== undefined && patch.status !== task.status) {
          const from = patch.status;
          const to = task.status;
          toast(statusChangedMessage(task.title, from, spawned), {
            action: { label: 'Undo', onAction: () => undoStatus(updated, from, to) },
          });
        }
      } catch (patchError: unknown) {
        if (patchStamps.current.get(task.id) !== sent) return;
        upsertTask(task);
        setActionError(
          patchError instanceof Error ? patchError.message : 'Could not update that task.',
        );
      }
    },
    [stamp, upsertTask, toast, undoStatus, flash],
  );

  // `undoStatus` is declared before `patchTask` and calls it, so it goes
  // through a ref rather than a dependency the two would share in a cycle.
  const patchTaskRef = useRef(patchTask);
  patchTaskRef.current = patchTask;

  /**
   * The same edit across the whole selection.
   *
   * There is no bulk endpoint, so this is N single-task PATCHes through a
   * concurrency pool. Both passes over the list are one `setTasks` each: doing
   * it row by row would be N re-renders of the list on the way out and a
   * half-applied batch flickering into place on the way back.
   */
  const patchSelected = useCallback(
    async (patch: RowPatch) => {
      const targets = tasks.filter((task) => selected.has(task.id));
      if (targets.length === 0) return;

      const sent = new Map(targets.map((task) => [task.id, stamp(task.id)]));
      const optimistic = new Map(targets.map((task) => [task.id, applyRowPatch(task, patch)]));
      setTasks((current) => current.map((task) => optimistic.get(task.id) ?? task));
      setActionError(null);

      const results = await mapPool(targets, BULK_CONCURRENCY, (task) =>
        updateTask(task.id, patch),
      );

      // The server's row where it worked, the row as it was where it did not.
      const settled = new Map<string, TaskSummary>();
      const failures: string[] = [];

      results.forEach((result, index) => {
        const before = targets[index]!;
        // A row someone edited individually while the batch was in flight owns
        // itself now; neither the reply nor the revert should touch it.
        if (patchStamps.current.get(before.id) !== sent.get(before.id)) return;

        if (result.status === 'fulfilled') {
          settled.set(before.id, result.value.task);
          putTask(result.value.task);
        } else {
          settled.set(before.id, before);
          failures.push(result.reason instanceof Error ? result.reason.message : '');
        }
      });

      setTasks((current) => current.map((task) => settled.get(task.id) ?? task));

      // One Undo for the batch, putting each task back to the status it had
      // before -- they were not all the same, so this is not one more bulk
      // patch. Only the rows that actually changed are offered.
      const nextStatus = patch.status;
      if (nextStatus !== undefined) {
        // In `settled` as something other than itself: the PATCH went through.
        const moved = targets.filter((before) => {
          const after = settled.get(before.id);
          return after !== undefined && after !== before && before.status !== nextStatus;
        });
        if (moved.length > 0) {
          toast(bulkStatusChangedMessage(moved.length, nextStatus), {
            action: {
              label: 'Undo',
              onAction: () => {
                for (const before of moved) {
                  undoStatus(settled.get(before.id)!, nextStatus, before.status);
                }
              },
            },
          });
        }
      }

      if (failures.length > 0) {
        // One line for the batch. When every failure said the same thing -- and
        // for the start-date rule they all will -- quote it, because the count
        // alone does not tell anyone what to change.
        const distinct = new Set(failures.filter(Boolean));
        const summary = `${failures.length} of ${targets.length} tasks were not updated.`;
        setActionError(distinct.size === 1 ? `${summary} ${[...distinct][0]}` : summary);
      }

      // The selection deliberately survives. Two changes to the same batch is
      // the common case, the ticks follow their rows into whatever group they
      // land in, and clearing here would take the bar down underneath the error
      // it had just raised.
    },
    [tasks, selected, stamp, toast, undoStatus],
  );

  /**
   * Tick or untick a row. A shift-click extends from the last plain click
   * instead, over the list's own reading order rather than the array's -- what
   * "between these two" means is what you can see.
   */
  const toggleSelected = useCallback(
    (task: TaskSummary, options: { range: boolean }) => {
      const anchor = selectionAnchor.current;

      setSelectedIds((current) => {
        if (options.range && anchor !== null && anchor !== task.id) {
          const ordered = orderForRef.current(tasks);
          const from = ordered.findIndex((entry) => entry.id === anchor);
          const to = ordered.findIndex((entry) => entry.id === task.id);

          if (from !== -1 && to !== -1) {
            const next = new Set(current);
            for (const entry of ordered.slice(Math.min(from, to), Math.max(from, to) + 1)) {
              if (!isPendingTask(entry.id)) next.add(entry.id);
            }
            return next;
          }
        }

        const next = new Set(current);
        if (next.has(task.id)) next.delete(task.id);
        else next.add(task.id);
        return next;
      });

      // A range extends from where the last plain click was, so only a plain
      // click moves it -- otherwise shift-clicking twice would walk the anchor
      // down the list and the second range would start somewhere unexpected.
      if (!options.range) selectionAnchor.current = task.id;
    },
    [tasks],
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(EMPTY_SELECTION);
    selectionAnchor.current = null;
  }, []);

  return {
    tasks,
    total,
    loading,
    refreshing,
    refresh,
    loadError,
    actionError,
    setActionError,
    failedCreates,
    dismissCreate,
    retryCreate,
    selected,
    toggleSelected,
    clearSelection,
    upsertTask,
    removeTask,
    createInBackground,
    patchTask,
    patchSelected,
    flashId,
  };
}
